import { cn } from '@/utlis/cn'
import { Slider as SliderArk } from '@ark-ui/react'

type SliderProps = {
    onChange?: (newValue: number) => void
    onChangeEnd?: (newValue: number) => void
    value?: number
    initialValue?: number
    disabled?: boolean
    min?: number
    max?: number
    step?: number
    markers?: boolean
    className?: string
}

const sliderThumbStyles = {
    enabled: 'hover:bg-[rgba(98,95,93,1)] active:bg-[rgba(78,75,73,1)] cursor-pointer',
    disabled: 'opacity-50 cursor-not-allowed',
}

export function Slider(props: SliderProps) {
    const showMarkers = props.markers ?? true

    return (
        <SliderArk.Root
            value={props.value ? [props.value] : undefined}
            defaultValue={props.initialValue ? [props.initialValue] : undefined}
            min={props.min ?? 0}
            max={props.max ?? 100}
            step={props.step ?? 1}
            disabled={props.disabled}
            onValueChange={(details) => {
                props.onChange?.(details.value[0])
            }}
            onValueChangeEnd={(details) => {
                props.onChangeEnd?.(details.value[0])
            }}
            className={cn('w-full', props.className)}
        >
            <SliderArk.Control className={cn(props.disabled ? 'opacity-50' : '', 'w-full h-6 flex items-center')}>
                <div className="w-full flex flex-col gap-[3px]">
                    {showMarkers && <Markers max={props.max ?? 100} min={props.min ?? 0} isAboveRange />}
                    <SliderArk.Track className="h-[2px] bg-[rgba(255,255,255,0.1)] rounded-full z-10">
                        <SliderArk.Range className="h-[2px] rounded-full bg-[rgba(133,224,163,1)]" />
                    </SliderArk.Track>
                    {showMarkers && <Markers max={props.max ?? 100} min={props.min ?? 0} />}
                </div>
                <SliderArk.Thumb
                    className={cn(
                        props.disabled ? sliderThumbStyles.disabled : sliderThumbStyles.enabled,
                        'bg-[rgba(88,85,83,1)] z-10 w-[10px] h-[24px] rounded-[6px] border-1 border-white/5 flex items-center justify-center'
                    )}
                    index={0}
                >
                    <div className="w-[2px] h-[14px] bg-[rgba(133,224,163,1)]" />
                    <SliderArk.HiddenInput />
                </SliderArk.Thumb>
            </SliderArk.Control>
        </SliderArk.Root>
    )
}

const markerStyles = {
    major: 'bg-[rgba(255,255,255,0.1)] rounded-[20px] w-[1px] h-[6px]',
    minor: 'bg-[rgba(255,255,255,0.1)] rounded-[20px] w-[1px] h-[3px]',
}

// TODO: Do the math to do the range here instead of enforcing markers be associated with input values of 0 to 100 to avoid rounding errors.
function Markers({ isAboveRange, max, min }: { isAboveRange?: boolean; min: number; max: number }) {
    // Split the marker into 9 total marks - inclusive of min and max.
    const markerValues = Array.from({ length: 9 }, (_, i) => i * ((max - min) / 8))

    return (
        <div className="w-full">
            <SliderArk.MarkerGroup className={cn('absolute inset-0 flex', isAboveRange ? 'items-end' : 'items-start')}>
                {markerValues.map((value) => (
                    <SliderArk.Marker
                        key={value}
                        className={value % ((max - min) / 4) === 0 ? markerStyles.major : markerStyles.minor}
                        value={value}
                    />
                ))}
            </SliderArk.MarkerGroup>
        </div>
    )
}
